'use client'
import { Auth } from 'aws-amplify'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { isLoggedIn } from '../../utils/auth'
import { getSanitizedRedirectUrls, LOGIN_STATUS_MESSAGE, SESSION_EXPIRED_KEY } from './utils'
import AuthWrapper from '../../components/Auth/AuthWrapper'
import { captureException } from '../../components/Sentry/utils'
import useLoginUser from '../../hooks/useLoginUser'
import { LoginVariantA } from '../LogIn/Variants/ExperimentA'
import { useCheckout } from '@src/quince-site-common/src/services/checkoutService/hooks/useCheckout'
import mixpanelCl from '@src/telemetry/integrations/mixpanel'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent, trackEvent } from '@src/telemetry/track'
import ForgotPasswordModal from '../../components/ForgotPasswordModal'
import { useRedirectToCheckoutIfApplicable } from './hooks/use-login-redirect'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { useLoginSideEffectsAndRedirect } from './hooks/use-login-sideeffects'
const StyledLogin = styled.div``

const LogIn = ({ location, data }) => {
  const router = useRouter()
  const { checkoutInstance: checkoutService, initialized } = useCheckout()
  const redirectToCheckoutIfApplicable = useRedirectToCheckoutIfApplicable()

  const page = data.authenticationPage
  const { loginUser, onGoogleFederatedLogIn } = useLoginUser()
  const [serverError, setServerError] = useState()

  const loginDownStreamSideEffectsWithRedirect = useLoginSideEffectsAndRedirect(setServerError)

  const [unverifiedAccount, setUnverifiedAccount] = useState(false)
  const [verificationEmailSent, setVerificationEmailSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState()
  const [resetPasswordModalState, setResetPasswordModalState] = useState(false)
  const params = new URLSearchParams(location.search)
  const resetPwPopup = params.get('reset-pw-popup')
  const resetPassword = params.get('reset-password')
  const sessionExpired = params.get('status') === SESSION_EXPIRED_KEY
  const isInResetPasswordFlow = resetPassword === 'true'

  useEffect(() => {
    if (isInResetPasswordFlow) return
    if (isLoggedIn()) {
      setLoading(true)
      const redirectUri = getSanitizedRedirectUrls()
      redirectToCheckoutIfApplicable(redirectUri)
      setLoading(false)
    }
  }, [isInResetPasswordFlow])

  useEffect(() => {
    if (sessionExpired) {
      setServerError({ message: LOGIN_STATUS_MESSAGE[SESSION_EXPIRED_KEY] })
    }
  }, [sessionExpired])

  useEffect(() => {
    if (resetPwPopup === 'true') {
      setResetPasswordModalState(true)
    }
  }, [resetPwPopup])

  const resendVerificationEmail = async email => {
    try {
      await Auth.resendSignUp(email?.toLowerCase())
      trackClickEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
        trackInternal: true,
        category: 'login',
        action: 'resend_verification',
        context: {
          login_email: email,
          lb_user_id: getlbUserId(),
        },
      })
    } catch (err) {
      captureException(err, { email }, { type: 'API_FAILURE', API: 'RESEND_VERIFICATION_EMAIL' })
    }
    setUnverifiedAccount(false)
    setVerificationEmailSent(true)
    setTimeout(() => {
      setVerificationEmailSent(false)
    }, 10000)
  }

  const backToLogin = () => {
    setServerError('')
  }

  const onGoogleLogIn = useCallback(
    async (user, callbackFn) => {
      if (!initialized) {
        return
      }
      setServerError('')
      setLoading(true)
      try {
        await onGoogleFederatedLogIn(user)
        trackEvent(TELEMETRY_EVENT_NAMES.FORM_SUBMIT, {
          trackInternal: true,
          category: 'login',
          action: 'account_login',
          context: {
            login_email: user.username,
            lb_user_id: getlbUserId(),
            login_method: 'SSO',
          },
        })
        mixpanelCl.identify({ email: user.username, isTentativeEmail: false })
        let redirectUri = getSanitizedRedirectUrls()
        const storedRedirectUri = sessionStorage.getItem('redirecturi')
        if (storedRedirectUri) {
          redirectUri = JSON.parse(storedRedirectUri)
          sessionStorage.removeItem('redirecturi')
        }
        await loginDownStreamSideEffectsWithRedirect(redirectUri)
        setLoading(false)
        callbackFn()
      } catch (err) {
        if (err && err.code == 'PASSWORD_RESET_NEEDED') {
          captureException(err, { data }, { type: 'PASSWORD_RESET_NEEDED', API: 'LOGIN' })
          router.push('?reset-pw-popup=true')
        }
        setLoading(false)
        setServerError(err)
        callbackFn()
      }
    },
    [initialized, onGoogleFederatedLogIn, checkoutService, data],
  )

  const openResetPasswordModal = email => {
    router.replace('?reset-pw-popup=true')
    setEmail(email)
  }

  const login = async data => {
    let { username, password, hasPasswordResetted = false } = data
    setServerError('')
    setLoading(true)

    if (username) {
      username = username?.toLowerCase()
    }
    setEmail(username)

    try {
      // Get customer access token
      // Sign in

      await loginUser(username, password, hasPasswordResetted)
      trackEvent(TELEMETRY_EVENT_NAMES.FORM_SUBMIT, {
        trackInternal: true,
        category: 'login',
        action: 'account_login',
        context: {
          login_email: username,
          lb_user_id: getlbUserId(),
          login_method: 'general',
        },
      })
      mixpanelCl.identify({ email: username, isTentativeEmail: false })
      await loginDownStreamSideEffectsWithRedirect()
    } catch (err) {
      setLoading(false)
      const errorCodesToIgnore = new Set(['UserNotFoundException', 'NotAuthorizedException'])

      /** No need of capturing above error events because they are not genuine issues */
      if (!errorCodesToIgnore.has(err.code)) {
        captureException(err, { data }, { type: 'API_FAILURE', API: 'LOGIN' })
      }

      if (err && err.code == 'UserNotFoundException') {
        err.message =
          'Hmm. We don\'t have that email on record. <br/> Please verify your email or <a href="/sign-up" style="text-decoration:underline;font-weight: 500">sign up</a> if your account does not exist.'
      } else if (err && err.code == 'UserNotConfirmedException') {
        try {
          setUnverifiedAccount(true)
          err.message =
            'Looks like your account is not verified. Please check your inbox for a verification link to complete the registration.'
        } catch (err) {
          err.message = 'User email not confirmed. Could not resend verification email.'
          captureException(`User email not confirmed. Could not resend verification email ${username}`)
        }
      } else if (err && err.code == 'PASSWORD_RESET_NEEDED') {
        captureException(err, { data }, { type: 'PASSWORD_RESET_NEEDED', API: 'LOGIN' })
        router.push('?reset-pw-popup=true')
      } else if (err && err.code == 'InternalErrorException') {
        err.message = 'Something went wrong. Please try logging in after some time.'
      } else if (err && err.code == 'PasswordResetRequiredException') {
        err.message = 'Unable to login. Please reset your password.'
      }
      setServerError(err)
    }
  }

  const onCloseForgotPassword = () => {
    router.replace('?reset-pw-popup=false')
    setResetPasswordModalState(false)
  }

  const forgotPasswordSubmitCallback = async (username, password) => {
    setResetPasswordModalState(false)
    await login({ username, password, hasPasswordResetted: true })
  }

  return (
    <>
      <StyledLogin>
        <AuthWrapper image={page.image} classNames="auth_wrapper_login">
          <LoginVariantA
            serverError={serverError}
            unverifiedAccount={unverifiedAccount}
            resendVerificationEmail={resendVerificationEmail}
            verificationEmailSent={verificationEmailSent}
            email={email}
            onGoogleLogIn={onGoogleLogIn}
            backToLogin={backToLogin}
            page={page}
            login={login}
            loading={loading}
            openResetPasswordModal={openResetPasswordModal}
            isInResetPasswordFlow={isInResetPasswordFlow}
          />
          <ForgotPasswordModal
            open={resetPasswordModalState}
            username={email}
            onClose={onCloseForgotPassword}
            onSubmitCallback={forgotPasswordSubmitCallback}
          />
        </AuthWrapper>
      </StyledLogin>
    </>
  )
}

export default LogIn
